import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Polyline } from "react-leaflet";
import L from "leaflet";
import { frontMiddleBack } from "../lib/greenFMB";

/**
 * RoundMapHero — embedded aerial mini-map shown at the top of the in-round
 * play screen. Renders the satellite view of the current hole with the
 * player → pin polyline, F/B green markers, and a distance bubble on the
 * line. Floating overlays show the hole pill (top-centre), GPS signal +
 * fullscreen button (top-right), big yardage badge (bottom-left), and a
 * compact F/M/B strip (bottom-right).
 *
 * For tap-to-target precision and a bigger workspace, the user opens the
 * existing fullscreen HoleView via the expand button.
 */

const METERS_TO_YARDS = 1.0936133;
const toRad = (d) => (d * Math.PI) / 180;
function distanceYds(a, b) {
  if (!a || !b) return null;
  const R = 6371000;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  const m = R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
  return Math.round(m * METERS_TO_YARDS);
}

const pinIcon = L.divIcon({
  className: "",
  html: `<div style="display:flex;flex-direction:column;align-items:center;transform:translateY(-100%)">
    <div style="background:#143D2F;color:#FFE45E;font-weight:900;font-size:10px;padding:2px 6px;border-radius:9999px;letter-spacing:0.05em;text-transform:uppercase;box-shadow:0 2px 6px rgba(0,0,0,0.45);white-space:nowrap">Pin</div>
    <div style="width:0;height:0;border-left:5px solid transparent;border-right:5px solid transparent;border-top:5px solid #143D2F;margin-top:-1px"></div>
  </div>`,
  iconSize: [28, 22],
  iconAnchor: [14, 22],
});

const playerIcon = L.divIcon({
  className: "",
  html: `<div style="position:relative;width:18px;height:18px">
    <div style="position:absolute;inset:-6px;border-radius:50%;background:rgba(59,130,246,0.25);animation:fairwayPulse 2s ease-out infinite"></div>
    <div style="position:relative;width:18px;height:18px;border-radius:50%;background:#3B82F6;border:3px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,0.5)"></div>
    <div style="position:absolute;left:50%;top:-22px;transform:translateX(-50%);background:#3B82F6;color:#fff;font-weight:900;font-size:9px;padding:2px 6px;border-radius:9999px;letter-spacing:0.05em;text-transform:uppercase;box-shadow:0 2px 6px rgba(0,0,0,0.45);white-space:nowrap;font-family:'DM Sans',sans-serif">You</div>
  </div>`,
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

function greenEdgeIcon(letter, color) {
  return L.divIcon({
    className: "",
    html: `<div style="width:16px;height:16px;border-radius:50%;background:${color};color:#fff;font-weight:900;font-size:9px;display:flex;align-items:center;justify-content:center;border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,0.5);font-family:'DM Sans',sans-serif">${letter}</div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });
}

// Palette for mate dots on the map — cycles by index so 3 mates
// each get a distinct colour without configuration.
const MATE_COLOURS = ["#EF4444", "#F59E0B", "#8B5CF6", "#10B981"]; // red / amber / violet / green

function mateIcon(name, color) {
  const initial = (name || "?").slice(0, 1).toUpperCase();
  return L.divIcon({
    className: "",
    html: `<div style="position:relative;width:16px;height:16px">
      <div style="position:relative;width:16px;height:16px;border-radius:50%;background:${color};color:#fff;font-weight:900;font-size:9px;display:flex;align-items:center;justify-content:center;border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,0.5);font-family:'DM Sans',sans-serif">${initial}</div>
      <div style="position:absolute;left:50%;top:-20px;transform:translateX(-50%);background:${color};color:#fff;font-weight:900;font-size:8px;padding:1px 5px;border-radius:9999px;letter-spacing:0.03em;text-transform:uppercase;box-shadow:0 2px 6px rgba(0,0,0,0.45);white-space:nowrap;font-family:'DM Sans',sans-serif">${name || "?"}</div>
    </div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });
}

function DistanceBubble({ pos, value }) {
  const icon = L.divIcon({
    className: "",
    html: `<div style="background:#143D2F;color:#FFE45E;font-weight:900;font-size:12px;font-variant-numeric:tabular-nums;padding:3px 8px;border-radius:9999px;border:2px solid rgba(255,255,255,0.85);box-shadow:0 2px 8px rgba(0,0,0,0.5);white-space:nowrap;font-family:'DM Sans',sans-serif">${value}</div>`,
    iconSize: [40, 22],
    iconAnchor: [20, 11],
  });
  return <Marker position={[pos.lat, pos.lng]} icon={icon} interactive={false} />;
}

export default function RoundMapHero({
  holeNumber,
  par,
  strokeIndex,
  cardYards,
  playerPos,
  matePositions,  // [{userId, lat, lng, name}] — other players in a fourball
  breadcrumb,
  pin,
  // Timestamp when the pin was last locked (ISO string). Used to render
  // a fading-confidence badge ("Fresh pin" → "2 days old" → "Old — help
  // us update?") so players can judge trust in the distance-to-pin value
  // without us pretending it's more accurate than it is.
  pinLockedAt,
  // Live wind data (from Open-Meteo via WeatherPill in the parent).
  //   { mph, windFromDeg (0-360, direction wind is COMING FROM),
  //     relativeDeg (0-360, wind direction relative to shot bearing) }
  // We overlay a compass-pinned arrow on the top-right of the map so
  // players can see wind direction + strength at a glance without
  // reading the pill's text label.
  wind,
  courseCenter, // { lat, lng } — used as the satellite-map fallback when this
                // hole has no pin coords yet, so we don't dump the player onto
                // the middle of England (54.5, -2.5) when a course is missing
                // pin data.
  distance,
  distanceSource, // "gps" | "course_default" | null
  gpsError,
  onExpand,
  onFixPin,
  vsPar,
  totalHoles,
  // Live scoring overlay — Stableford points or match-play status, rendered
  // in the bottom-right corner so the score stays visible on the map without
  // scrolling. `undefined` hides the chip (e.g. strokeplay rounds).
  scoreChip,
  // Distance the player has walked from where they opened this hole (their
  // "tee" reference). Used to display a live-updating counterpart to the
  // main distance badge (e.g. "TO PIN ~230 yds" when we only have a card
  // estimate, or "FROM TEE 152 yds" when the main is real GPS).
  walkedFromTee,
}) {
  const mapRef = useRef(null);
  const hasGps = playerPos && pin;

  const bounds = useMemo(() => {
    const pts = [];
    if (playerPos) pts.push([playerPos.lat, playerPos.lng]);
    if (pin) pts.push([pin.lat, pin.lng]);
    if (pts.length === 0) return null;
    if (pts.length === 1) {
      const [lat, lng] = pts[0];
      return L.latLngBounds([lat - 0.001, lng - 0.001], [lat + 0.001, lng + 0.001]);
    }
    return L.latLngBounds(pts);
  }, [playerPos, pin]);

  useEffect(() => {
    if (!mapRef.current || !bounds) return;
    mapRef.current.fitBounds(bounds, { padding: [40, 40], maxZoom: 18 });
  }, [bounds]);

  // Initial map centre. Priority: hole pin → player's live position →
  // course centroid → UK geographic centre (last-ditch fallback). Anything
  // beats dumping the user on an empty field.
  const initialCenter = pin
    ? [pin.lat, pin.lng]
    : playerPos
      ? [playerPos.lat, playerPos.lng]
      : courseCenter
        ? [courseCenter.lat, courseCenter.lng]
        : [54.5, -2.5];
  const initialZoom = pin || playerPos ? 16 : courseCenter ? 15 : 6;
  const midpoint =
    playerPos && pin
      ? { lat: (playerPos.lat + pin.lat) / 2, lng: (playerPos.lng + pin.lng) / 2 }
      : null;
  const distPin = distanceYds(playerPos, pin);
  const fmb =
    playerPos && pin ? frontMiddleBack(playerPos.lat, playerPos.lng, pin.lat, pin.lng) : null;

  // Show the satellite map whenever we have ANY anchor — the hole pin, the
  // course centroid, or the player's live GPS. Only fall back to the plain
  // dark-green panel if we genuinely have nothing to centre on.
  const haveAnchor = !!(pin || courseCenter || playerPos);
  if (!haveAnchor) {
    return (
      <div
        className="relative rounded-[2rem] overflow-hidden shadow-xl"
        style={{ background: "#143D2F", height: 280 }}
        data-testid="round-map-hero-fallback"
      >
        <div className="absolute inset-0 opacity-25 bg-[radial-gradient(circle_at_top_right,_#FFE45E,_transparent_60%)]" />
        <HeaderPill holeNumber={holeNumber} par={par} cardYards={cardYards} totalHoles={totalHoles} />
        <div className="absolute left-5 bottom-5 text-white">
          <p className="text-[10px] uppercase tracking-[0.16em] opacity-70">Distance to pin</p>
          <div className="font-mono-num font-black text-6xl leading-none mt-1" data-testid="round-map-hero-distance">
            {distance ?? "—"}
            <span className="text-base font-heading font-bold ml-2 opacity-90">yds</span>
          </div>
          <p className="text-[10px] uppercase tracking-wider opacity-60 mt-2">
            No GPS coords for this hole — card yardage shown
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative rounded-[2rem] overflow-hidden shadow-xl"
      style={{ height: 320, background: "#143D2F" }}
      data-testid="round-map-hero"
    >
      <MapContainer
        center={initialCenter}
        zoom={initialZoom}
        zoomControl={false}
        attributionControl={false}
        dragging={false}
        scrollWheelZoom={false}
        doubleClickZoom={false}
        touchZoom={false}
        boxZoom={false}
        keyboard={false}
        style={{ width: "100%", height: "100%" }}
        ref={mapRef}
      >
        <TileLayer
          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          attribution=""
          maxNativeZoom={19}
          maxZoom={20}
        />
        {pin && <Marker position={[pin.lat, pin.lng]} icon={pinIcon} />}
        {/* Breadcrumb trail — where the player has walked on this hole. */}
        {breadcrumb && breadcrumb.length >= 2 && (
          <Polyline
            positions={breadcrumb.map((p) => [p.lat, p.lng])}
            pathOptions={{ color: "#60A5FA", weight: 3, opacity: 0.7, dashArray: "2 6", lineCap: "round" }}
          />
        )}
        {playerPos && <Marker position={[playerPos.lat, playerPos.lng]} icon={playerIcon} />}
        {matePositions && matePositions.map((m, i) => (
          <Marker
            key={m.userId}
            position={[m.lat, m.lng]}
            icon={mateIcon(m.name, MATE_COLOURS[i % MATE_COLOURS.length])}
            interactive={false}
          />
        ))}
        {fmb && (
          <>
            <Marker
              position={[fmb.frontPos.lat, fmb.frontPos.lng]}
              icon={greenEdgeIcon("F", "#22c55e")}
              interactive={false}
            />
            <Marker
              position={[fmb.backPos.lat, fmb.backPos.lng]}
              icon={greenEdgeIcon("B", "#0ea5e9")}
              interactive={false}
            />
          </>
        )}
        {playerPos && pin && (
          <Polyline
            positions={[
              [playerPos.lat, playerPos.lng],
              [pin.lat, pin.lng],
            ]}
            pathOptions={{ color: "#FFE45E", weight: 3, opacity: 0.9, dashArray: "6 6" }}
          />
        )}
        {midpoint && distPin != null && <DistanceBubble pos={midpoint} value={distPin} />}
      </MapContainer>

      {/* Subtle bottom-to-top dark gradient so text remains legible over bright fairway tiles */}
      <div
        className="absolute inset-x-0 bottom-0 pointer-events-none"
        style={{
          height: "55%",
          background: "linear-gradient(to top, rgba(20,61,47,0.85) 5%, rgba(20,61,47,0.25) 60%, transparent 100%)",
          zIndex: 400,
        }}
      />
      <div
        className="absolute inset-x-0 top-0 pointer-events-none"
        style={{
          height: "30%",
          background: "linear-gradient(to bottom, rgba(0,0,0,0.45) 0%, transparent 100%)",
          zIndex: 400,
        }}
      />

      {/* Top-centre hole pill */}
      <HeaderPill
        holeNumber={holeNumber}
        par={par}
        strokeIndex={strokeIndex}
        cardYards={cardYards}
        totalHoles={totalHoles}
      />

      {/* Top-right: GPS chip + expand button */}
      <div
        className="absolute top-3 right-3 flex items-center gap-1.5"
        style={{ zIndex: 500 }}
      >
        <GpsChip source={distanceSource} hasError={!!gpsError} />
        <button
          type="button"
          onClick={onExpand}
          aria-label="Open fullscreen map"
          data-testid="round-map-hero-expand"
          className="h-9 w-9 rounded-full bg-white/15 backdrop-blur-md text-white flex items-center justify-center shadow-lg active:scale-95 transition-transform"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
          </svg>
        </button>
      </div>

      {/* Bottom-left: live scoring chip + chunky distance badge + vs-par
          chip. Score chip lives here (not top-right) so it doesn't collide
          with the hole-info strip at the top of the map. */}
      <div className="absolute left-4 bottom-3 text-white" style={{ zIndex: 500 }}>
        {scoreChip && (
          <div
            className="inline-flex items-center gap-2 bg-black/60 backdrop-blur-md rounded-xl px-2.5 py-1 mb-1.5 shadow-lg"
            data-testid="round-map-hero-score-chip"
          >
            <span className="text-[9px] uppercase tracking-[2px] opacity-80 font-bold">
              {scoreChip.label}
            </span>
            <span className="font-mono-num font-black text-sm leading-none tracking-tighter-2">
              {scoreChip.value}
              {scoreChip.unit && (
                <span className="text-[9px] font-bold opacity-70 ml-0.5">{scoreChip.unit}</span>
              )}
            </span>
          </div>
        )}
        <p className="text-[10px] uppercase tracking-[0.16em] opacity-80 leading-none">
          {distanceSource === "course_default" ? "From tee (est.)" : "To pin"}
        </p>
        <div className="flex items-baseline gap-1.5 mt-0.5">
          <div
            className={`font-mono-num font-black text-5xl leading-none tracking-tighter-2 ${distanceSource === "course_default" ? "opacity-75" : ""}`}
            data-testid="round-map-hero-distance"
          >
            {distanceSource === "course_default" ? `~${distance ?? "—"}` : (distance ?? "—")}
          </div>
          <div className="font-heading font-bold text-sm opacity-90">yds</div>
        </div>
        {distanceSource === "course_default" && (
          <p
            className="text-[9.5px] uppercase tracking-[0.14em] font-bold text-[#FFE45E] mt-1 leading-tight max-w-[14rem]"
            data-testid="round-map-hero-no-pin-hint"
          >
            No pin locked — tap &quot;Help us pin this hole&quot; so this becomes live
          </p>
        )}
        {distanceSource === "gps" && pinLockedAt && (
          <PinAgeBadge lockedAt={pinLockedAt} onFixPin={onFixPin} />
        )}

        {/* Live counterpart chip. When the main badge is "TO PIN" (real GPS),
            we show "FROM TEE" here so players see both. When main is the
            static "FROM TEE (EST.)", we synthesize a "TO PIN ~X" that counts
            DOWN as they walk (card yards minus walked distance). */}
        {typeof walkedFromTee === "number" && distance != null && (
          <div
            className="mt-2 inline-flex items-center gap-1.5 bg-black/45 backdrop-blur-md rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider"
            data-testid="round-map-hero-counterpart"
          >
            {distanceSource === "course_default" ? (
              <>
                <span className="opacity-70">To pin ~</span>
                <span className="font-mono-num text-white text-[13px]">
                  {Math.max(0, Math.round(distance - walkedFromTee))}
                </span>
                <span className="opacity-70">yds</span>
              </>
            ) : (
              <>
                <span className="opacity-70">From tee</span>
                <span className="font-mono-num text-white text-[13px]">
                  {Math.round(walkedFromTee)}
                </span>
                <span className="opacity-70">yds</span>
              </>
            )}
          </div>
        )}

        {vsPar !== undefined && vsPar !== null && (
          <div className="mt-1.5 inline-flex items-center gap-1 bg-black/40 backdrop-blur-md rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">
            <span className="opacity-70">vs par</span>
            <span style={{ color: vsPar > 0 ? "#fda4af" : vsPar < 0 ? "#86efac" : "#FFE45E" }}>
              {vsPar > 0 ? `+${vsPar}` : vsPar === 0 ? "E" : vsPar}
            </span>
          </div>
        )}
      </div>

      {/* Score chip has moved to bottom-left (inside the distance cluster)
          so it doesn't collide with the top hole-info strip. See there. */}

      {/* Top-right: live wind compass arrow. Direction shows where the
          wind is COMING FROM (meteorology convention). Strength encoded
          via the arrow-body colour: green (calm), amber (breezy), red
          (gusty). Hidden entirely when wind data isn't available. */}
      {wind && wind.mph != null && wind.windFromDeg != null && (
        <WindCompass
          mph={wind.mph}
          gustMph={wind.gustMph}
          windFromDeg={wind.windFromDeg}
          relativeDeg={wind.relativeDeg}
        />
      )}


      {/* Bottom-right: F/M/B strip */}
      {fmb && (
        <div
          className="absolute right-3 bottom-3 bg-black/55 backdrop-blur-md rounded-2xl px-2.5 py-2 flex flex-col gap-1.5"
          style={{ zIndex: 500 }}
          data-testid="round-map-hero-fmb"
        >
          {[
            { label: "F", val: fmb.front, color: "#22c55e" },
            { label: "M", val: fmb.middle, color: "#FFE45E" },
            { label: "B", val: fmb.back, color: "#0ea5e9" },
          ].map((s) => (
            <div key={s.label} className="flex items-center gap-1.5">
              <span
                className="text-[9px] font-black uppercase tracking-wider px-1 py-0.5 rounded leading-none"
                style={{ background: s.color, color: "#143D2F", minWidth: 14, textAlign: "center" }}
              >
                {s.label}
              </span>
              <span className="font-mono-num font-black text-white text-sm leading-none tabular-nums">
                {s.val}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* "Fix pin" tap zone — only when course flagged stats_unknown.
          Opens the fullscreen HoleView in pin-correction mode. */}
      {onFixPin && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onFixPin(); }}
          data-testid="round-map-hero-fix-pin"
          className="absolute top-3 left-3 inline-flex items-center gap-1 bg-amber-500/90 backdrop-blur-md text-white text-[9px] font-black uppercase tracking-wider px-2 py-1 rounded-full shadow-lg active:scale-95 transition-transform"
          style={{ zIndex: 500 }}
        >
          ⚑ Fix pin
        </button>
      )}
    </div>
  );
}

function HeaderPill({ holeNumber, par, strokeIndex, cardYards, totalHoles }) {
  return (
    <div
      className="absolute top-3 left-3 right-3 sm:right-auto bg-black/60 backdrop-blur-md text-white rounded-2xl px-3.5 py-2 shadow-lg flex items-center gap-2.5 flex-wrap"
      style={{ zIndex: 500 }}
      data-testid="round-map-hero-header"
    >
      <div className="font-heading font-black text-base leading-none tabular-nums">
        {holeNumber}
        <sup className="text-[9px] font-bold ml-0.5">{ordinal(holeNumber)}</sup>
      </div>
      <div className="w-px h-4 bg-white/25" />
      <div className="text-xs uppercase tracking-wider opacity-90 leading-none">
        Par <span className="font-bold text-white text-sm">{par}</span>
      </div>
      {strokeIndex ? (
        <div
          className="text-xs uppercase tracking-wider opacity-90 leading-none"
          data-testid="round-map-hero-si"
          title={`Stroke index ${strokeIndex} — the ${ordinalWord(strokeIndex)}-hardest hole. You get a handicap shot here if your handicap ≥ ${strokeIndex}.`}
        >
          SI <span className="font-bold text-white text-sm">{strokeIndex}</span>
        </div>
      ) : null}
      <div className="text-xs uppercase tracking-wider opacity-90 leading-none">
        <span className="font-mono-num font-bold text-white text-sm">{cardYards}</span> yds
      </div>
      {totalHoles && (
        <>
          <div className="w-px h-4 bg-white/25" />
          <div className="text-xs uppercase tracking-wider opacity-70 leading-none tabular-nums">
            {holeNumber}/{totalHoles}
          </div>
        </>
      )}
    </div>
  );
}

function ordinalWord(n) {
  const words = ["first","second","third","fourth","fifth","sixth","seventh","eighth","ninth","tenth","eleventh","twelfth","thirteenth","fourteenth","fifteenth","sixteenth","seventeenth","eighteenth"];
  return words[n - 1] || `${n}th`;
}

function GpsChip({ source, hasError }) {
  let bg = "rgba(0,0,0,0.55)";
  let dot = "#FFE45E";
  let label = "GPS";
  if (hasError) {
    bg = "rgba(239,68,68,0.85)";
    dot = "#fff";
    label = "GPS OFF";
  } else if (source === "gps") {
    bg = "rgba(34,197,94,0.85)";
    dot = "#fff";
    label = "GPS LIVE";
  } else if (source === "course_default") {
    bg = "rgba(251,191,36,0.85)";
    dot = "#fff";
    label = "CARD";
  }
  return (
    <div
      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full backdrop-blur-md text-white text-[9px] font-black uppercase tracking-wider shadow-lg"
      style={{ background: bg }}
      data-testid="round-map-hero-gps"
    >
      <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: dot }} />
      {label}
    </div>
  );
}

function ordinal(n) {
  if (n == null) return "";
  const s = ["TH", "ST", "ND", "RD"];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}

/**
 * Wind compass — top-right badge with a compass arrow showing where the
 * wind is COMING FROM (meteorology convention). Arrow body colour
 * indicates severity:
 *   • Green   — ≤ 8 mph (calm)
 *   • Amber   — 8-15 mph (breezy, adjust ½ club)
 *   • Red     — > 15 mph (gusty, adjust 1+ club)
 * Also shows the mph number so players don't have to eyeball the colour.
 *
 * The rotation math: SVG defaults arrow-up (0°). `windFromDeg` is
 * north-referenced (0° = due north). Rotating by `windFromDeg` makes the
 * tail point in the wind's origin direction — which is exactly the club
 * scoreboard convention.
 */
function WindCompass({ mph, gustMph, windFromDeg, relativeDeg }) {
  const severity =
    mph > 15 ? "gust" :
    mph > 8 ? "breeze" :
    "calm";
  const colour =
    severity === "gust" ? "rgba(239, 68, 68, 0.9)" :
    severity === "breeze" ? "rgba(234, 179, 8, 0.9)" :
    "rgba(34, 197, 94, 0.9)";
  const roundedMph = Math.round(mph);
  // Rough "into your face" hint when the wind is coming FROM the pin
  // direction (within 45°). Uses the pre-computed relativeDeg from the
  // WeatherPill so we don't recompute bearings here.
  let intoLabel = "";
  if (relativeDeg != null) {
    if (relativeDeg > 315 || relativeDeg < 45) intoLabel = "In your face";
    else if (relativeDeg > 135 && relativeDeg < 225) intoLabel = "At your back";
    else if (relativeDeg >= 45 && relativeDeg <= 135) intoLabel = "L→R crosswind";
    else intoLabel = "R→L crosswind";
  }
  return (
    <div
      className="absolute right-3 top-3 bg-black/60 backdrop-blur-md rounded-2xl px-2.5 py-1.5 shadow-lg flex items-center gap-2"
      style={{ zIndex: 500 }}
      data-testid="round-map-hero-wind"
      title={`${roundedMph} mph${gustMph ? ` (gusts ${Math.round(gustMph)})` : ""}${intoLabel ? ` · ${intoLabel}` : ""}`}
    >
      <svg
        viewBox="0 0 24 24"
        width="22"
        height="22"
        style={{ transform: `rotate(${windFromDeg}deg)`, transition: "transform 400ms ease-out" }}
        aria-label={`Wind from ${Math.round(windFromDeg)}°`}
      >
        {/* Arrow pointing UP by default. Head at top, tail at bottom. */}
        <path
          d="M12 3 L18 12 L14 12 L14 21 L10 21 L10 12 L6 12 Z"
          fill={colour}
          stroke="rgba(255,255,255,0.5)"
          strokeWidth="0.6"
        />
      </svg>
      <div className="text-white leading-none">
        <div className="font-mono-num font-black text-sm tracking-tighter-2">{roundedMph}</div>
        <div className="text-[8px] uppercase font-bold opacity-70">mph</div>
      </div>
    </div>
  );
}


/**
 * Pin-age confidence badge.
 *
 * Greenkeepers cut a new hole every 1-3 days, so a "pin" from more than a
 * few days ago probably isn't where the flag is now. Rather than lying to
 * the player by showing yesterday's coords as gospel — or hiding useful
 * data by expiring the pin abruptly — we show the pin AND its age with a
 * colour that fades from green → yellow → orange → grey.
 *
 * The pin coordinates always keep flowing; this badge just tells the
 * player how much to trust them.
 */
function PinAgeBadge({ lockedAt, onFixPin }) {
  const [, tick] = useState(0);
  // Re-render once a minute so the "2h ago" label updates while the
  // player is on the tee. React strict-mode-safe cleanup.
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 60000);
    return () => clearInterval(id);
  }, []);

  const ageMs = Date.now() - new Date(lockedAt).getTime();
  if (!Number.isFinite(ageMs) || ageMs < 0) return null;
  const hours = ageMs / 3_600_000;

  let colour = "rgba(34, 197, 94, 0.85)";  // green
  let label = "Fresh pin";
  let hint = "";
  if (hours >= 72) {
    colour = "rgba(148, 163, 184, 0.85)";  // grey
    label = "Old pin — help us update?";
    hint = "tap ⚑ Fix pin";
  } else if (hours >= 48) {
    colour = "rgba(249, 115, 22, 0.85)";   // orange
    label = "Pin ~2 days old";
    hint = "aim centre-of-green";
  } else if (hours >= 24) {
    colour = "rgba(234, 179, 8, 0.85)";    // yellow
    label = "Yesterday's pin";
    hint = "";
  } else {
    // Fresh — show relative time to make the freshness feel real.
    const mins = Math.round(ageMs / 60000);
    if (mins < 60) label = `Fresh pin · ${mins}m ago`;
    else label = `Fresh pin · ${Math.floor(hours)}h ago`;
  }

  const isClickable = hours >= 72 && typeof onFixPin === "function";
  const Tag = isClickable ? "button" : "div";
  return (
    <Tag
      type={isClickable ? "button" : undefined}
      onClick={isClickable ? onFixPin : undefined}
      className="mt-1 inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white"
      style={{ background: colour }}
      data-testid="round-map-hero-pin-age"
      title={`Pin last locked ${new Date(lockedAt).toLocaleString("en-GB")}`}
    >
      <span>{label}</span>
      {hint && <span className="opacity-80 normal-case">· {hint}</span>}
    </Tag>
  );
}
